import React, { useState, useEffect } from 'react';
import { getAllSponsors, createSponsor, updateSponsor, deleteSponsor } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import AdminLayout from '../components/AdminLayout';

const AdminSponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subtitle: '',
    logo_url: '',
    website_url: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    loadSponsors();
  }, []);

  const loadSponsors = async () => {
    try {
      const data = await getAllSponsors();
      setSponsors(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading sponsors:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateSponsor(editing, formData);
      } else {
        await createSponsor(formData);
      }
      loadSponsors();
      resetForm();
    } catch (error) {
      console.error('Error saving sponsor:', error);
      alert('Error saving sponsor');
    }
  };

  const handleEdit = (sponsor) => {
    setEditing(sponsor._id);
    setFormData({
      name: sponsor.name,
      subtitle: sponsor.subtitle || '',
      logo_url: sponsor.logo_url || '',
      website_url: sponsor.website_url || '',
      order: sponsor.order,
      active: sponsor.active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this sponsor?')) {
      try {
        await deleteSponsor(id);
        loadSponsors();
      } catch (error) {
        console.error('Error deleting sponsor:', error);
        alert('Error deleting sponsor');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subtitle: '',
      logo_url: '',
      website_url: '',
      order: 0,
      active: true
    });
    setEditing(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-white p-8">Loading...</div>;
  }

  return (
    <AdminLayout currentPage="sponsors">
      {/* Add Button */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Sponsor
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="mb-8 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">
              {editing ? 'Edit Sponsor' : 'Add New Sponsor'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Sponsor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Okta Ventures"
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Subtitle
                    </label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      placeholder="e.g., Identity Platform, Venture Capital"
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Logo URL
                    </label>
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Website URL
                    </label>
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-slate-300">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="w-4 h-4"
                      />
                      Active (visible on site)
                    </label>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {editing ? 'Update' : 'Create'} Sponsor
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* List */}
        <div className="grid gap-4">
          {sponsors.length === 0 ? (
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-8 text-center">
                <p className="text-slate-400">No sponsors yet. Add your first sponsor!</p>
              </CardContent>
            </Card>
          ) : (
            sponsors.map((sponsor) => (
              <Card key={sponsor._id} className="bg-slate-900 border-slate-800">
                <CardContent className="p-6">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      {sponsor.logo_url ? (
                        <img 
                          src={sponsor.logo_url} 
                          alt={sponsor.name}
                          className="h-10 w-auto object-contain"
                        />
                      ) : (
                        <div className="h-10 w-10 bg-slate-800 rounded flex items-center justify-center text-slate-500">
                          {sponsor.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-bold text-white">{sponsor.name}</h3>
                          {sponsor.subtitle && (
                            <span className="text-sm text-slate-400">• {sponsor.subtitle}</span>
                          )}
                          {!sponsor.active && (
                            <Badge className="bg-red-500/10 text-red-400 border-red-500/30">Inactive</Badge>
                          )}
                        </div>
                        {sponsor.website_url && (
                          <a 
                            href={sponsor.website_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                          >
                            {sponsor.website_url}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(sponsor)}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(sponsor._id)}
                        className="border-red-800 text-red-400 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
    </AdminLayout>
  );
};

export default AdminSponsors;
